// src/components/MatchForm.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react'
import STIPULATIONS from '../../data/stipulations'
import CHAMPIONSHIPS from '../../data/championships'
import { normalizeMatchTitleValues } from '../../utils/matchTitles'
import '../../styles/ShowEditor.css'

const BASE_STEP_ORDER = ['participants', 'stipulation', 'title', 'winner']

const STEP_META = {
  participants: { label: 'Partecipanti', icon: 'bi-person-plus' },
  stipulation: { label: 'Stipulazione', icon: 'bi-gear' },
  title: { label: 'Titolo', icon: 'bi-award' },
  winner: { label: 'Vincitore', icon: 'bi-trophy' }
}

const normalizeGender = value => {
  const normalized = String(value || '').trim().toLowerCase()
  if (['uomo', 'male', 'm', 'man'].includes(normalized)) return 'male'
  if (['donna', 'female', 'f', 'woman'].includes(normalized)) return 'female'
  return ''
}

const getChampionshipByValue = value =>
  CHAMPIONSHIPS.find(championship => championship.value === value) || null

const getChampionTitles = wrestler => {
  const values = [
    ...(Array.isArray(wrestler?.championTypes) ? wrestler.championTypes : []),
    wrestler?.currentChampionship,
    wrestler?.championship
  ]

  return [...new Set(values.filter(Boolean))]
    .filter(value => value !== 'Campione')
    .filter(value => getChampionshipByValue(value))
}

function MatchForm ({
  availableWrestlersForMatches,
  matchParticipants,
  onAddParticipant,
  onRemoveParticipant,
  matchStipulation,
  onStipulationChange,
  isTitleMatch,
  onTitleMatchChange,
  matchTitle,
  onTitleChange,
  secondaryMatchTitle,
  onSecondaryTitleChange,
  isMultiTeamMatch,
  onMultiTeamMatchChange,
  teams,
  onAddTeam,
  onRemoveTeam,
  onAddToTeam,
  onRemoveFromTeam,
  winner,
  onWinnerChange,
  isDisqualificationWin,
  onDisqualificationWinChange,
  guestMap,
  onAddGuest,
  savedGuests = [],
  savedTeams = [],
  onAddSavedTeam,
  onSubmit,
  onCancel,
  editingMatchId
}) {
  const [guestName, setGuestName] = useState('')
  const [guestImageUrl, setGuestImageUrl] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [focusParticipantId, setFocusParticipantId] = useState('')
  const [showOtherGenderWrestlers, setShowOtherGenderWrestlers] = useState(false)
  const [customStipulation, setCustomStipulation] = useState('')
  const [activeStep, setActiveStep] = useState('participants')
  const lastAutoStipulationRef = useRef('')
  const stepOrder = useMemo(
    () =>
      isTitleMatch
        ? BASE_STEP_ORDER
        : BASE_STEP_ORDER.filter(step => step !== 'title'),
    [isTitleMatch]
  )

  const isCustomStipulation = Boolean(
    matchStipulation && !STIPULATIONS.includes(matchStipulation)
  )

  const stipulationOptions = useMemo(() => {
    const options = [...STIPULATIONS]
    if (isCustomStipulation) options.push(matchStipulation)
    return options
  }, [isCustomStipulation, matchStipulation])

  const isNoContest = winner === 'no_contest'
  const selectedParticipantSet = useMemo(
    () => new Set(matchParticipants),
    [matchParticipants]
  )

  const wrestlerById = useMemo(
    () =>
      availableWrestlersForMatches.reduce((map, wrestler) => {
        map.set(wrestler.id, wrestler)
        return map
      }, new Map()),
    [availableWrestlersForMatches]
  )

  const selectedParticipantDetails = useMemo(
    () =>
      matchParticipants.map(participantId => {
        const wrestler = wrestlerById.get(participantId)
        const guest = guestMap[participantId]
        return {
          id: participantId,
          name: guest?.name || wrestler?.name || 'Sconosciuto',
          isGuest: Boolean(guest),
          participationStatus: wrestler?.participationStatus || null,
          hasLiveParticipation: Boolean(wrestler?.hasLiveParticipation),
          hasDraftParticipation: Boolean(wrestler?.hasDraftParticipation),
          gender: wrestler?.gender,
          normalizedGender: normalizeGender(wrestler?.gender),
          hasMatchBonus: Boolean(wrestler?.hasMatchBonus),
          hasDraftMatchBonus: Boolean(wrestler?.hasDraftMatchBonus),
          matchBonusCount: wrestler?.matchBonusCount || 0,
          hasWinBonus: Boolean(wrestler?.hasWinBonus),
          hasDraftWinBonus: Boolean(wrestler?.hasDraftWinBonus),
          hasLiveWinBonus: Boolean(wrestler?.hasLiveWinBonus),
          winBonusCount: wrestler?.winBonusCount || 0,
          championTitles: getChampionTitles(wrestler)
        }
      }),
    [guestMap, matchParticipants, wrestlerById]
  )

  const draftSelectedCount = selectedParticipantDetails.filter(
    item => item.hasDraftParticipation || item.participationStatus === 'draft'
  ).length

  const unassignedWrestlers = useMemo(() => {
    const assignedIds = new Set(teams.flatMap(team => team.participants))
    return matchParticipants.filter(id => !assignedIds.has(id))
  }, [matchParticipants, teams])

  const totalTeamParticipants = useMemo(
    () => teams.reduce((sum, team) => sum + team.participants.length, 0),
    [teams]
  )

  const selectedGenderSet = useMemo(
    () =>
      new Set(
        selectedParticipantDetails
          .map(participant => participant.normalizedGender)
          .filter(Boolean)
      ),
    [selectedParticipantDetails]
  )

  const hasMixedParticipants =
    selectedGenderSet.has('male') && selectedGenderSet.has('female')

  const suggestedStipulation = useMemo(() => {
    const teamsWithParticipants = teams.filter(team => team.participants.length > 0)

    if (isMultiTeamMatch) {
      if (teamsWithParticipants.length === 2) return 'Tag Team'
      if (teamsWithParticipants.length === 3) return 'Triple Threat Tag Team'
      if (teamsWithParticipants.length === 4) return 'Fatal 4-Way Tag Team'
      return ''
    }

    if (matchParticipants.length === 2) return 'Single'
    if (matchParticipants.length === 3) return 'Triple Threat'
    if (matchParticipants.length === 4) return 'Fatal 4-Way'
    if (matchParticipants.length === 6) return 'Six-Pack Challenge'
    return ''
  }, [isMultiTeamMatch, matchParticipants.length, teams])

  const eligibleTitleOptions = useMemo(() => {
    const allowedTypes = hasMixedParticipants
      ? new Set(['mixed_tag_team'])
      : isMultiTeamMatch
      ? new Set(['tag_team', 'trios'])
      : new Set(['singles'])

    return CHAMPIONSHIPS.filter(championship => {
      if (!championship.value) return false
      if (!allowedTypes.has(championship.type)) return false
      if (hasMixedParticipants) return championship.gender === 'mixed'
      if (selectedGenderSet.size === 0) return true
      if (championship.gender === 'mixed') return false
      return selectedGenderSet.has(championship.gender)
    })
  }, [hasMixedParticipants, isMultiTeamMatch, selectedGenderSet])

  const selectedMatchTitleValues = useMemo(
    () => normalizeMatchTitleValues(matchTitle, secondaryMatchTitle),
    [matchTitle, secondaryMatchTitle]
  )

  const titleOptions = useMemo(() => {
    const selectedTitleOptions = selectedMatchTitleValues
      .map(getChampionshipByValue)
      .filter(Boolean)

    return [...selectedTitleOptions, ...eligibleTitleOptions].filter(
      (championship, index, collection) =>
        collection.findIndex(item => item.value === championship.value) === index
    )
  }, [eligibleTitleOptions, selectedMatchTitleValues])

  const secondaryTitleOptions = useMemo(
    () => titleOptions.filter(championship => championship.value !== matchTitle),
    [matchTitle, titleOptions]
  )

  const suggestedTitle = useMemo(() => {
    const eligibleValues = new Set(eligibleTitleOptions.map(title => title.value))
    const championTitle = selectedParticipantDetails
      .flatMap(participant => participant.championTitles)
      .find(title => eligibleValues.has(title))

    if (championTitle) return championTitle
    if (hasMixedParticipants) {
      return eligibleTitleOptions.find(title => title.type === 'mixed_tag_team')?.value || ''
    }
    return ''
  }, [eligibleTitleOptions, hasMixedParticipants, selectedParticipantDetails])

  const focusParticipant = useMemo(
    () =>
      selectedParticipantDetails.find(
        participant =>
          participant.id === focusParticipantId && !participant.isGuest
      ) ||
      selectedParticipantDetails.find(participant => !participant.isGuest) ||
      null,
    [focusParticipantId, selectedParticipantDetails]
  )
  const focusGender = focusParticipant?.normalizedGender || ''
  const focusGenderLabel =
    focusGender === 'male'
      ? 'uomini'
      : focusGender === 'female'
      ? 'donne'
      : ''
  const alternateGenderLabel =
    focusGender === 'male'
      ? 'donne'
      : focusGender === 'female'
      ? 'uomini'
      : ''
  const shouldRestrictByFocusGender =
    Boolean(focusGender) &&
    !showOtherGenderWrestlers &&
    !hasMixedParticipants

  const suggestedOpponentIds = useMemo(() => {
    if (!focusParticipant) return []

    return availableWrestlersForMatches
      .filter(wrestler => !selectedParticipantSet.has(wrestler.id))
      .filter(wrestler => {
        if (!focusGender) return true
        return normalizeGender(wrestler.gender) === focusGender
      })
      .sort((first, second) => {
        if (first.hasAssignedMatch !== second.hasAssignedMatch) {
          return first.hasAssignedMatch ? 1 : -1
        }
        if (first.hasMatchBonus !== second.hasMatchBonus) {
          return first.hasMatchBonus ? -1 : 1
        }
        if (first.hasDraftMatchBonus !== second.hasDraftMatchBonus) {
          return first.hasDraftMatchBonus ? -1 : 1
        }
        if (first.participationStatus !== second.participationStatus) {
          return first.participationStatus === 'live' ? -1 : 1
        }
        return first.name.localeCompare(second.name)
      })
      .map(wrestler => wrestler.id)
  }, [
    availableWrestlersForMatches,
    focusGender,
    focusParticipant,
    selectedParticipantSet
  ])

  const filteredWrestlers = useMemo(() => {
    const query = searchQuery.toLowerCase().trim()
    const filtered = query
      ? availableWrestlersForMatches.filter(
          wrestler =>
            wrestler.name.toLowerCase().includes(query) ||
            (wrestler.brand || '').toLowerCase().includes(query)
        )
      : availableWrestlersForMatches
    const genderFiltered = shouldRestrictByFocusGender
      ? filtered.filter(
          wrestler =>
            selectedParticipantSet.has(wrestler.id) ||
            normalizeGender(wrestler.gender) === focusGender
        )
      : filtered

    const suggestedIdSet = new Set(suggestedOpponentIds)

    return [...genderFiltered].sort((first, second) => {
      const firstSelected = selectedParticipantSet.has(first.id)
      const secondSelected = selectedParticipantSet.has(second.id)
      if (firstSelected !== secondSelected) return firstSelected ? -1 : 1

      const firstSuggested = suggestedIdSet.has(first.id)
      const secondSuggested = suggestedIdSet.has(second.id)
      if (firstSuggested !== secondSuggested) return firstSuggested ? -1 : 1

      if (first.hasAssignedMatch !== second.hasAssignedMatch) {
        return first.hasAssignedMatch ? 1 : -1
      }

      if (first.hasMatchBonus !== second.hasMatchBonus) {
        return first.hasMatchBonus ? -1 : 1
      }

      if (first.hasDraftMatchBonus !== second.hasDraftMatchBonus) {
        return first.hasDraftMatchBonus ? -1 : 1
      }

      if (first.participationStatus !== second.participationStatus) {
        return first.participationStatus === 'live' ? -1 : 1
      }

      return first.name.localeCompare(second.name)
    })
  }, [
    availableWrestlersForMatches,
    focusGender,
    searchQuery,
    selectedParticipantSet,
    shouldRestrictByFocusGender,
    suggestedOpponentIds
  ])

  const teamSuggestions = useMemo(() => {
    const availableIds = new Set(
      availableWrestlersForMatches.map(wrestler => wrestler.id)
    )
    const assignedIds = new Set(teams.flatMap(team => team.participants))
    const selectedIds = new Set(matchParticipants)
    const selectedOrAssignedIds = new Set([
      ...matchParticipants,
      ...assignedIds
    ])

    return savedTeams
      .map(team => {
        const members = (team.members || []).filter(memberId =>
          availableIds.has(memberId)
        )
        const selectableMembers = isMultiTeamMatch
          ? members.filter(memberId => !assignedIds.has(memberId))
          : members.filter(memberId => !selectedIds.has(memberId))
        const selectedMembers = members.filter(memberId =>
          selectedOrAssignedIds.has(memberId)
        )
        return {
          ...team,
          members,
          selectableMembers,
          selectedMembers
        }
      })
      .filter(team => {
        if (team.members.length === 0 || team.selectableMembers.length === 0) return false
        return isMultiTeamMatch || team.selectedMembers.length > 0
      })
  }, [
    availableWrestlersForMatches,
    isMultiTeamMatch,
    matchParticipants,
    savedTeams,
    teams
  ])

  const participantsReady =
    (!isMultiTeamMatch && matchParticipants.length >= 2) ||
    (isMultiTeamMatch &&
      totalTeamParticipants >= 2 &&
      teams.every(team => team.participants.length > 0) &&
      unassignedWrestlers.length === 0)

  const stipulationReady = Boolean(matchStipulation)
  const titleReady = !isTitleMatch || Boolean(matchTitle)
  const canSubmit = participantsReady && stipulationReady && titleReady

  const activeStepIndex = stepOrder.indexOf(activeStep)
  const canGoNext =
    (activeStep === 'participants' && participantsReady) ||
    (activeStep === 'stipulation' && stipulationReady) ||
    (activeStep === 'title' && titleReady)

  useEffect(() => {
    const selectableParticipants = selectedParticipantDetails.filter(
      participant => !participant.isGuest
    )
    const hasCurrentFocus = selectableParticipants.some(
      participant => participant.id === focusParticipantId
    )

    if (hasCurrentFocus) return

    const nextFocus = selectableParticipants[selectableParticipants.length - 1]
    setFocusParticipantId(nextFocus?.id || '')
  }, [focusParticipantId, selectedParticipantDetails])

  useEffect(() => {
    if (!focusGender || matchParticipants.length === 0) {
      setShowOtherGenderWrestlers(false)
    }
  }, [focusGender, matchParticipants.length])

  useEffect(() => {
    if (isCustomStipulation) {
      setCustomStipulation(matchStipulation)
      return
    }

    setCustomStipulation('')
  }, [isCustomStipulation, matchStipulation])

  useEffect(() => {
    if (!suggestedStipulation) return

    if (!matchStipulation || matchStipulation === lastAutoStipulationRef.current) {
      lastAutoStipulationRef.current = suggestedStipulation
      onStipulationChange(suggestedStipulation)
    }
  }, [matchStipulation, onStipulationChange, suggestedStipulation])

  useEffect(() => {
    setActiveStep('participants')
  }, [editingMatchId])

  useEffect(() => {
    if (
      editingMatchId ||
      matchParticipants.length > 0 ||
      matchStipulation ||
      secondaryMatchTitle ||
      matchTitle ||
      winner ||
      isTitleMatch
    ) {
      return
    }

    setActiveStep('participants')
  }, [
    editingMatchId,
    isTitleMatch,
    matchParticipants.length,
    matchStipulation,
    matchTitle,
    secondaryMatchTitle,
    winner
  ])

  useEffect(() => {
    if (!stepOrder.includes(activeStep)) {
      setActiveStep(stepOrder[stepOrder.length - 1] || 'participants')
    }
  }, [activeStep, stepOrder])

  const handleAddGuest = () => {
    if (!guestName) return
    const gid = `guest_${Date.now()}_${Math.floor(Math.random() * 1000)}`
    onAddGuest(gid, guestName, guestImageUrl)
    onAddParticipant(gid)
    setGuestName('')
    setGuestImageUrl('')
  }

  const alreadyAddedGuestNames = useMemo(
    () =>
      new Set(
        matchParticipants
          .map(id => guestMap[id]?.name)
          .filter(Boolean)
          .map(name => name.toLowerCase())
      ),
    [guestMap, matchParticipants]
  )

  const guestSuggestions = useMemo(() => {
    const normalizedQuery = guestName.trim().toLowerCase()
    if (!normalizedQuery) return []

    return savedGuests
      .filter(guest => guest.name.toLowerCase().includes(normalizedQuery))
      .filter(guest => !alreadyAddedGuestNames.has(guest.name.toLowerCase()))
      .slice(0, 8)
  }, [alreadyAddedGuestNames, guestName, savedGuests])

  const handleSelectGuestSuggestion = guest => {
    const gid = `guest_${Date.now()}_${Math.floor(Math.random() * 1000)}`
    onAddGuest(gid, guest.name, guest.imageUrl || '')
    onAddParticipant(gid)
    setGuestName('')
    setGuestImageUrl('')
  }

  const handleWrestlerToggle = wrestlerId => {
    if (matchParticipants.includes(wrestlerId)) {
      onRemoveParticipant(wrestlerId, false)
    } else {
      onAddParticipant(wrestlerId)
    }
  }

  const handleSelectableRowKeyDown = (event, wrestlerId) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    handleWrestlerToggle(wrestlerId)
  }

  const getParticipantDisplayName = React.useCallback(participantId => {
    const guest = guestMap[participantId]
    const wrestler = wrestlerById.get(participantId)
    return guest?.name || wrestler?.name || 'Sconosciuto'
  }, [guestMap, wrestlerById])

  const getTeamName = React.useCallback((team, fallback = 'Squadra') => {
    if (!team.participants.length) return team.name || fallback
    return team.participants.map(getParticipantDisplayName).join(' & ')
  }, [getParticipantDisplayName])

  const handleWinnerChange = value => {
    onWinnerChange(value)
    if (!value || value === 'no_contest') {
      onDisqualificationWinChange(false)
    }
  }

  const handleTitleMatchChange = nextValue => {
    onTitleMatchChange(nextValue)

    if (!nextValue) {
      onTitleChange('')
      onSecondaryTitleChange('')
    }
  }

  const handleTitleChange = value => {
    const nextTitles = normalizeMatchTitleValues(value, secondaryMatchTitle)
    onTitleChange(nextTitles[0] || '')
    onSecondaryTitleChange(nextTitles[1] || '')
  }

  const handleSecondaryTitleChange = value => {
    const nextTitles = normalizeMatchTitleValues(matchTitle, value)
    onTitleChange(nextTitles[0] || '')
    onSecondaryTitleChange(nextTitles[1] || '')
  }

  const handleNext = () => {
    if (!canGoNext) return
    setActiveStep(
      stepOrder[Math.min(activeStepIndex + 1, stepOrder.length - 1)]
    )
  }

  const handleBack = () => {
    setActiveStep(stepOrder[Math.max(activeStepIndex - 1, 0)])
  }

  const handleSubmit = event => {
    if (activeStep !== 'winner') {
      event.preventDefault()
      handleNext()
      return
    }

    onSubmit(event)
  }

  const winnerOptions = isMultiTeamMatch
    ? teams.map(team => ({
        value: `team${team.id}`,
        label: getTeamName(team),
        disabled: team.participants.length === 0
      }))
    : matchParticipants.map(wrestlerId => ({
        value: wrestlerId,
        label: getParticipantDisplayName(wrestlerId),
        disabled: false
      }))

  const suggestedWinner = useMemo(() => {
    if (isMultiTeamMatch) {
      const teamsWithWinBonus = teams
        .map(team => {
          const winBonusCount = (team.participants || []).reduce(
            (sum, participantId) =>
              sum +
              (selectedParticipantDetails.find(
                participant => participant.id === participantId
              )?.winBonusCount || 0),
            0
          )

          return {
            value: `team${team.id}`,
            label: getTeamName(team),
            winBonusCount
          }
        })
        .filter(team => team.winBonusCount > 0)

      if (teamsWithWinBonus.length !== 1) return null

      return teamsWithWinBonus[0]
    }

    const participantsWithWinBonus = selectedParticipantDetails.filter(
      participant => participant.winBonusCount > 0
    )

    if (participantsWithWinBonus.length !== 1) return null

    return {
      value: participantsWithWinBonus[0].id,
      label: participantsWithWinBonus[0].name,
      winBonusCount: participantsWithWinBonus[0].winBonusCount
    }
  }, [getTeamName, isMultiTeamMatch, selectedParticipantDetails, teams])

  const renderStepNav = () => (
    <div className='show-match-stepper' role='tablist' aria-label='Step inserimento match'>
      {stepOrder.map((step, index) => {
        const meta = STEP_META[step]
        const isActive = step === activeStep
        const isDone = index < activeStepIndex

        return (
          <button
            key={step}
            type='button'
            className={`show-match-stepper__item ${isActive ? 'is-active' : ''} ${isDone ? 'is-done' : ''}`}
            onClick={() => setActiveStep(step)}
            role='tab'
            aria-selected={isActive}
          >
            <span className='show-match-stepper__number'>{index + 1}</span>
            <span className='show-match-stepper__label'>
              <i className={`bi ${meta.icon} me-1`}></i>
              {meta.label}
            </span>
          </button>
        )
      })}
    </div>
  )

  const renderParticipantsStep = () => (
    <div className='show-match-step-panel'>
      <div className='mb-4 show-editor-section'>
        <label className='form-label fw-semibold d-block'>
          <i className='bi bi-award me-1'></i>
          Match titolato
        </label>
        <div className='show-match-choice-grid show-match-choice-grid--duo'>
          <button
            type='button'
            className={`btn ${!isTitleMatch ? 'btn-brand-primary' : 'btn-outline-brand-primary text-light'}`}
            onClick={() => handleTitleMatchChange(false)}
          >
            <i className='bi bi-slash-circle me-1'></i>
            No, match normale
          </button>
          <button
            type='button'
            className={`btn ${isTitleMatch ? 'btn-brand-secondary' : 'btn-outline-brand-secondary'}`}
            onClick={() => handleTitleMatchChange(true)}
          >
            <i className='bi bi-award me-1'></i>
            Si, titolo in palio
          </button>
        </div>
        <small className='text-muted d-block mt-2'>
          Se attivi questa opzione, dopo la stipulazione potrai scegliere un titolo principale e un secondo titolo opzionale.
        </small>
      </div>

      <div className='mb-4 show-editor-section'>
        <label className='form-label fw-semibold d-block'>
          <i className='bi bi-diagram-3 me-1'></i>
          Tipo match
        </label>
        <div className='btn-group w-100' role='group' aria-label='Tipo match'>
          <button
            type='button'
            className={`btn ${!isMultiTeamMatch ? 'btn-success' : 'btn-outline-success'}`}
            onClick={() => onMultiTeamMatchChange(false)}
          >
            <i className='bi bi-person me-1'></i>
            Singolo/Multi-man
          </button>
          <button
            type='button'
            className={`btn ${isMultiTeamMatch ? 'btn-success' : 'btn-outline-success'}`}
            onClick={() => onMultiTeamMatchChange(true)}
          >
            <i className='bi bi-people-fill me-1'></i>
            A squadre
          </button>
        </div>
      </div>

      {isMultiTeamMatch && teamSuggestions.length > 0 && (
        <div className='border rounded p-2 mb-3 bg-light show-editor-panel show-editor-panel--compact'>
          <div className='d-flex align-items-center justify-content-between mb-2'>
            <small className='fw-semibold text-muted'>
              <i className='bi bi-lightning-charge-fill me-1'></i>
              Team rapidi
            </small>
            <small className='text-muted'>Carica una squadra salvata</small>
          </div>
          <div className='d-flex flex-wrap gap-2'>
            {teamSuggestions.map(team => (
              <button
                key={team.id}
                type='button'
                className='btn btn-outline-success btn-sm'
                onClick={() => onAddSavedTeam && onAddSavedTeam(team)}
                title={team.memberNames?.join(', ') || team.name}
              >
                <i className='bi bi-plus-circle me-1'></i>
                {team.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className='mb-4 show-editor-section'>
        <label className='form-label fw-semibold'>
          <i className='bi bi-person-plus me-1'></i>
          Partecipanti
          <small className='text-muted ms-2'>({matchParticipants.length} selezionati)</small>
        </label>

        <div className='input-group mb-3'>
          <span className='input-group-text'>
            <i className='bi bi-search'></i>
          </span>
          <input
            type='text'
            className='form-control'
            placeholder='Cerca wrestler...'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type='button'
              className='btn btn-outline-secondary'
              onClick={() => setSearchQuery('')}
              aria-label='Cancella ricerca wrestler'
            >
              <i className='bi bi-x-lg'></i>
            </button>
          )}
        </div>

        {focusParticipant && suggestedOpponentIds.length > 0 && (
          <div className='show-match-focus-panel mb-3'>
            <div className='show-match-focus-panel__header'>
              <div>
                <strong>
                  <i className='bi bi-magic me-2'></i>
                  Suggeriti contro {focusParticipant.name}
                </strong>
              </div>
              <small className='text-muted'>
                {suggestedOpponentIds.length} disponibili
              </small>
            </div>
            <div className='show-match-suggestions'>
              {suggestedOpponentIds.slice(0, 10).map(wrestlerId => {
                const wrestler = wrestlerById.get(wrestlerId)
                if (!wrestler) return null

                return (
                  <button
                    key={wrestler.id}
                    type='button'
                    className='btn btn-outline-light btn-sm show-match-suggestion'
                    onClick={() => handleWrestlerToggle(wrestler.id)}
                  >
                    <span>{wrestler.name}</span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {focusGender && (
          <div className='show-editor-panel mb-3'>
            <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2'>
              <div>
                <strong>
                  {hasMixedParticipants
                    ? 'Match mixed attivo'
                    : shouldRestrictByFocusGender
                    ? `Lista filtrata su ${focusGenderLabel}`
                    : 'Lista mixed attiva'}
                </strong>
                <small className='d-block text-muted mt-1'>
                  {hasMixedParticipants
                    ? "Hai gia' selezionato wrestler di entrambi i sessi, quindi la lista resta aperta."
                    : shouldRestrictByFocusGender
                    ? `Per creare un match mixed puoi mostrare anche ${alternateGenderLabel}.`
                    : `Stai vedendo anche ${alternateGenderLabel} per aggiungere wrestler dell'altro sesso.`}
                </small>
              </div>
              {!hasMixedParticipants && (
                <button
                  type='button'
                  className={`btn btn-sm ${
                    shouldRestrictByFocusGender
                      ? 'btn-outline-warning'
                      : 'btn-warning text-dark'
                  }`}
                  onClick={() =>
                    setShowOtherGenderWrestlers(currentValue => !currentValue)
                  }
                >
                  {shouldRestrictByFocusGender
                    ? `Mostra anche ${alternateGenderLabel}`
                    : `Torna a solo ${focusGenderLabel}`}
                </button>
              )}
            </div>
          </div>
        )}

        <div className='mb-3'>
          <div
            className='border rounded show-editor-scroll-list show-editor-scroll-list--tall'
            style={{ maxHeight: '500px', overflowY: 'auto' }}
          >
            {filteredWrestlers.length === 0 ? (
              <div className='text-center py-3 text-muted show-editor-empty'>
                {searchQuery ? 'Nessun wrestler trovato' : 'Nessun wrestler disponibile'}
              </div>
            ) : (
              <div className='list-group list-group-flush'>
                {filteredWrestlers.map(wrestler => {
                  const isSelected = selectedParticipantSet.has(wrestler.id)
                  const isSuggested = suggestedOpponentIds.includes(wrestler.id)

                  return (
                    <div
                      key={wrestler.id}
                      className={`list-group-item list-group-item-action py-2 px-3 show-editor-list-item show-match-wrestler-row ${
                        isSelected ? 'show-match-wrestler-row--selected' : ''
                      } ${wrestler.hasMatchBonus ? 'show-match-wrestler-row--bonus' : ''} ${
                        isSuggested ? 'show-match-wrestler-row--suggested' : ''
                      }`}
                      onClick={() => handleWrestlerToggle(wrestler.id)}
                      onKeyDown={event => handleSelectableRowKeyDown(event, wrestler.id)}
                      role='button'
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className='d-flex align-items-center gap-2'>
                        <input
                          type='checkbox'
                          className='form-check-input mt-0'
                          checked={isSelected}
                          onChange={() => handleWrestlerToggle(wrestler.id)}
                          onClick={e => e.stopPropagation()}
                          id={`wrestler-${wrestler.id}`}
                        />
                        <label
                          className='form-check-label flex-grow-1 mb-0'
                          htmlFor={`wrestler-${wrestler.id}`}
                          onClick={e => e.stopPropagation()}
                          style={{ cursor: 'pointer' }}
                          title={wrestler.name}
                        >
                          <div className='show-match-wrestler-row__main'>
                            <span>{wrestler.name}</span>
                          </div>
                        </label>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {matchParticipants.length > 0 && (
            <div className='mt-3'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <small className='text-muted'>
                  <i className='bi bi-check2-circle me-1'></i>
                  {matchParticipants.length} selezionati
                </small>
                <button
                  type='button'
                  className='btn btn-link btn-sm text-decoration-none p-0'
                  onClick={() => {
                    matchParticipants.forEach(id => onRemoveParticipant(id, false))
                  }}
                  disabled={matchParticipants.length === 0}
                >
                  <small className='text-danger'>
                    <i className='bi bi-x-circle me-1'></i>
                    Deseleziona tutti
                  </small>
                </button>
              </div>
              <div className='show-editor-chip-row'>
                {selectedParticipantDetails.map(participant => (
                  <span
                    key={participant.id}
                    className={`badge border-0 d-inline-flex align-items-center show-match-chip ${
                      focusParticipant?.id === participant.id ? 'show-match-chip--focus' : ''
                    } ${
                      participant.hasDraftParticipation || participant.participationStatus === 'draft'
                        ? 'show-match-chip--draft'
                        : participant.isGuest
                        ? 'show-match-chip--guest'
                        : 'show-match-chip--live'
                    }`}
                    onClick={() => !participant.isGuest && setFocusParticipantId(participant.id)}
                    onKeyDown={event => {
                      if (participant.isGuest) return
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      setFocusParticipantId(participant.id)
                    }}
                    role={participant.isGuest ? undefined : 'button'}
                    tabIndex={participant.isGuest ? undefined : 0}
                    title={
                      participant.isGuest
                        ? participant.name
                        : `Usa ${participant.name} come focus suggerimenti`
                    }
                  >
                    {participant.name}
                    <button
                      type='button'
                      className={`btn-close ms-1 ${
                        participant.hasDraftParticipation || participant.isGuest ? '' : 'btn-close-white'
                      }`}
                      onClick={event => {
                        event.stopPropagation()
                        onRemoveParticipant(participant.id, participant.isGuest)
                      }}
                      aria-label={`Rimuovi ${participant.name}`}
                      style={{ fontSize: '0.55rem' }}
                    />
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {!isMultiTeamMatch && teamSuggestions.length > 0 && (
          <div className='border rounded p-2 mb-3 bg-light show-editor-panel show-editor-panel--compact'>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <small className='fw-semibold text-muted'>
                <i className='bi bi-people-fill me-1'></i>
                Team
              </small>
              <small className='text-muted'>Seleziona membri</small>
            </div>
            <div className='d-flex flex-wrap gap-2'>
              {teamSuggestions.map(team => (
                <button
                  key={team.id}
                  type='button'
                  className='btn btn-outline-success btn-sm'
                  onClick={() => onAddSavedTeam && onAddSavedTeam(team)}
                  title={team.memberNames?.join(', ') || team.name}
                >
                  <i className='bi bi-plus-circle me-1'></i>
                  {team.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {!searchQuery && (
          <div className='border rounded p-3 show-editor-panel'>
            <label className='form-label fw-semibold small'>
              <i className='bi bi-star me-1'></i>
              Aggiungi Ospite Esterno
            </label>
            <div className='row g-2 align-items-end'>
              <div className='col-md-5 position-relative'>
                <input
                  type='text'
                  className='form-control form-control-sm'
                  placeholder='Cerca o inserisci nome ospite...'
                  value={guestName}
                  onChange={e => setGuestName(e.target.value)}
                  autoComplete='off'
                />
                {guestSuggestions.length > 0 && (
                  <div
                    className='list-group show-guest-suggestions position-absolute w-100 shadow-sm'
                    style={{ zIndex: 20, top: '100%' }}
                  >
                    {guestSuggestions.map(guest => (
                      <button
                        key={guest.name}
                        type='button'
                        className='list-group-item list-group-item-action d-flex align-items-center gap-2 show-guest-suggestion-item'
                        onClick={() => handleSelectGuestSuggestion(guest)}
                      >
                        {guest.imageUrl ? (
                          <img
                            src={guest.imageUrl}
                            alt=''
                            style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                          />
                        ) : (
                          <i className='bi bi-person-circle text-muted' style={{ fontSize: '1.4rem' }}></i>
                        )}
                        <span className='small flex-grow-1'>{guest.name}</span>
                        {guest.source === 'db' ? (
                          <i className='bi bi-bookmark-star-fill text-warning' title='Ospite salvato'></i>
                        ) : (
                          <i className='bi bi-clock-history text-muted' title='Gia usato in uno show precedente'></i>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className='col-md-5'>
                <input
                  type='text'
                  className='form-control form-control-sm'
                  placeholder='URL immagine (opzionale)'
                  value={guestImageUrl}
                  onChange={e => setGuestImageUrl(e.target.value)}
                />
              </div>
              <div className='col-md-2'>
                <button
                  type='button'
                  className='btn btn-outline-primary btn-sm w-100 show-guest-add-btn'
                  onClick={handleAddGuest}
                  disabled={!guestName}
                >
                  <i className='bi bi-plus'></i>
                  <span className='d-md-none ms-1'>Aggiungi ospite</span>
                </button>
              </div>
              {guestImageUrl ? (
                <div className='wrestler-image-preview'>
                  <img src={guestImageUrl} alt='Anteprima ospite' />
                </div>
              ) : (
                <div className=''></div>
              )}
            </div>
          </div>
        )}
      </div>

      {isMultiTeamMatch && (
        <div className='mb-4 show-editor-section'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <label className='form-label fw-semibold mb-0'>
              <i className='bi bi-people me-1'></i>
              Squadre
            </label>
            <button
              type='button'
              className='btn btn-outline-primary btn-sm'
              onClick={onAddTeam}
              disabled={teams.length >= 6}
            >
              <i className='bi bi-plus-circle me-1'></i>
              Nuova Squadra
            </button>
          </div>

          <div className='row g-3'>
            {teams.map((team, index) => (
              <div key={team.id} className='col-md-6'>
                <div className={`card border-${team.color} shadow-sm show-editor-team-card`}>
                  <div className={`card-header bg-${team.color} text-white py-2 d-flex justify-content-between align-items-center`}>
                    <div className='show-editor-team-name'>
                      <strong>{getTeamName(team, `Squadra ${index + 1}`)}</strong>
                      <small>
                        {team.participants.length > 0
                          ? `${team.participants.length} wrestler`
                          : `Squadra ${index + 1}`}
                      </small>
                    </div>
                    {teams.length > 1 && (
                      <button
                        type='button'
                        className='btn btn-outline-light btn-sm'
                        onClick={() => onRemoveTeam(team.id)}
                      >
                        <i className='bi bi-x'></i>
                      </button>
                    )}
                  </div>
                  <div className='card-body p-3'>
                    <div className='mb-3'>
                      <small className='text-muted d-block mb-2'>
                        Wrestler in squadra ({team.participants.length})
                      </small>
                      <div className='d-flex flex-wrap gap-1'>
                        {team.participants.map(wrestlerId => (
                          <span
                            key={wrestlerId}
                            className={`badge bg-${team.color} d-flex align-items-center`}
                          >
                            {getParticipantDisplayName(wrestlerId)}
                            <button
                              type='button'
                              className='btn-close btn-close-white ms-1'
                              onClick={() => onRemoveFromTeam(team.id, wrestlerId)}
                              aria-label='Rimuovi'
                            ></button>
                          </span>
                        ))}
                      </div>
                    </div>

                    {unassignedWrestlers.length > 0 && (
                      <div>
                        <small className='text-muted d-block mb-2'>
                          Aggiungi wrestler:
                        </small>
                        <div className='d-flex flex-wrap gap-1'>
                          {unassignedWrestlers.map(wrestlerId => (
                            <button
                              key={wrestlerId}
                              type='button'
                              className={`btn btn-outline-${team.color} btn-sm`}
                              onClick={() => onAddToTeam(team.id, wrestlerId)}
                            >
                              {getParticipantDisplayName(wrestlerId)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {unassignedWrestlers.length > 0 && (
            <div className='mt-3 p-3 border rounded bg-light show-editor-panel show-editor-panel--warning'>
              <label className='form-label fw-semibold small mb-2'>
                <i className='bi bi-clock me-1'></i>
                Wrestler da assegnare: {unassignedWrestlers.length}
              </label>
              <div className='d-flex flex-wrap gap-1'>
                {unassignedWrestlers.map(wrestlerId => (
                  <span key={wrestlerId} className='badge bg-secondary'>
                    {getParticipantDisplayName(wrestlerId)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )

  const renderStipulationStep = () => (
    <div className='show-match-step-panel'>
      <div className='mb-4 show-editor-section'>
        <label className='form-label fw-semibold'>
          <i className='bi bi-gear me-1'></i>
          Stipulazione
        </label>

        {suggestedStipulation && (
          <div className='show-match-autofill mb-3'>
            <span>
              <i className='bi bi-stars me-1'></i>
              Suggerita automaticamente: <strong>{suggestedStipulation}</strong>
            </span>
            <button
              type='button'
              className='btn btn-outline-light btn-sm'
              onClick={() => onStipulationChange(suggestedStipulation)}
            >
              Conferma
            </button>
          </div>
        )}

        <div className='show-match-choice-grid'>
          {stipulationOptions.slice(0, 10).map((st, index) => (
            <button
              key={`${st}-${index}`}
              type='button'
              className={`btn ${matchStipulation === st ? 'btn-brand-primary' : 'btn-outline-secondary'}`}
              onClick={() => onStipulationChange(st)}
            >
              {st}
            </button>
          ))}
        </div>

        <div className='d-grid gap-2 mt-3'>
          <select
            className='form-select'
            value={isCustomStipulation ? '__custom__' : matchStipulation}
            onChange={e => {
              if (e.target.value === '__custom__') {
                onStipulationChange(customStipulation)
                return
              }

              onStipulationChange(e.target.value)
            }}
            required
          >
            <option value=''>Seleziona...</option>
            {stipulationOptions.map((st, index) => (
              <option key={`${st}-${index}`} value={st}>
                {st}
              </option>
            ))}
            <option value='__custom__'>+ Nuova stipulazione...</option>
          </select>

          {(isCustomStipulation || matchStipulation === '') && (
            <input
              type='text'
              className='form-control'
              placeholder='Inserisci una stipulazione personalizzata'
              value={customStipulation}
              onChange={e => {
                const nextValue = e.target.value
                setCustomStipulation(nextValue)
                onStipulationChange(nextValue)
              }}
            />
          )}
        </div>
      </div>
    </div>
  )

  const renderTitleStep = () => (
    <div className='show-match-step-panel'>
      <div className='mb-4 show-editor-section'>
        <label className='form-label fw-semibold'>
          <i className='bi bi-award me-1'></i>
          Titoli in palio
        </label>

        <small className='text-muted d-block mb-3'>
          Scegli il titolo principale. Se serve, puoi aggiungerne un secondo in modo opzionale.
        </small>

        {suggestedTitle && (
          <div className='show-match-autofill mb-3'>
            <span>
              <i className='bi bi-stars me-1'></i>
              Suggerito: <strong>{getChampionshipByValue(suggestedTitle)?.name || suggestedTitle}</strong>
            </span>
            <button
              type='button'
              className='btn btn-outline-light btn-sm'
              onClick={() => handleTitleChange(suggestedTitle)}
            >
              Conferma
            </button>
          </div>
        )}

        {titleOptions.length > 0 && (
          <div className='show-match-title-stack'>
            <div className='show-match-title-block'>
              <div className='show-match-title-block__head'>
                <strong>Titolo principale</strong>
                <small>obbligatorio</small>
              </div>
              <div className='show-match-choice-grid show-match-choice-grid--titles'>
                {titleOptions.map(championship => (
                  <button
                    key={championship.value}
                    type='button'
                    className={`btn ${matchTitle === championship.value ? 'btn-brand-secondary' : 'btn-outline-brand-secondary'}`}
                    onClick={() => handleTitleChange(championship.value)}
                  >
                    {championship.name}
                  </button>
                ))}
              </div>
            </div>

            <div className='show-match-title-block'>
              <div className='show-match-title-block__head'>
                <strong>Secondo titolo</strong>
                <small>opzionale</small>
              </div>
              <div className='show-match-choice-grid show-match-choice-grid--titles'>
                <button
                  type='button'
                  className={`btn ${!secondaryMatchTitle ? 'btn-brand-primary' : 'btn-outline-brand-primary text-light'}`}
                  onClick={() => handleSecondaryTitleChange('')}
                >
                  Nessun secondo titolo
                </button>
                {secondaryTitleOptions.map(championship => (
                  <button
                    key={`secondary-${championship.value}`}
                    type='button'
                    className={`btn ${secondaryMatchTitle === championship.value ? 'btn-brand-secondary' : 'btn-outline-brand-secondary'}`}
                    onClick={() => handleSecondaryTitleChange(championship.value)}
                  >
                    {championship.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {titleOptions.length === 0 && (
          <div className='alert alert-info mt-3 py-2 small mb-0'>
            <i className='bi bi-info-circle me-1'></i>
            Nessun titolo compatibile con partecipanti e tipo match.
          </div>
        )}
      </div>
    </div>
  )

  const renderWinnerStep = () => (
    <div className='show-match-step-panel'>
      <div className='mb-4 show-editor-section'>
        <label className='form-label fw-semibold'>
          <i className='bi bi-trophy me-1'></i>
          Vincitore
          <small className='text-muted ms-2'>opzionale</small>
        </label>

        {suggestedWinner && (
          <div className='show-match-autofill mb-3'>
            <span>
              <i className='bi bi-stars me-1'></i>
              Suggerito dal bonus vittoria:{' '}
              <strong>{suggestedWinner.label}</strong>
            </span>
            <button
              type='button'
              className='btn btn-outline-light btn-sm'
              onClick={() => handleWinnerChange(suggestedWinner.value)}
            >
              Imposta vincitore
            </button>
          </div>
        )}

        <div className='show-match-choice-grid'>
          <button
            type='button'
            className={`btn ${!winner ? 'btn-success' : 'btn-outline-success'}`}
            onClick={() => handleWinnerChange('')}
          >
            Da definire
          </button>
          {winnerOptions.map(option => (
            <button
              key={option.value}
              type='button'
              className={`btn ${winner === option.value ? 'btn-success' : 'btn-outline-success'}`}
              onClick={() => handleWinnerChange(option.value)}
              disabled={option.disabled}
            >
              {option.label}
            </button>
          ))}
          <button
            type='button'
            className={`btn ${winner === 'no_contest' ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={() => handleWinnerChange('no_contest')}
          >
            No-Contest
          </button>
        </div>

        <div className='form-check form-switch mt-3'>
          <input
            className='form-check-input'
            type='checkbox'
            id='disqualificationWin'
            checked={isDisqualificationWin}
            onChange={e => onDisqualificationWinChange(e.target.checked)}
            disabled={!winner || isNoContest}
          />
          <label className='form-check-label small' htmlFor='disqualificationWin'>
            <i className='bi bi-flag me-1'></i>
            Vittoria per Squalifica
          </label>
        </div>

        {winner === 'no_contest' && (
          <div className='alert alert-warning mt-2 py-2 small mb-0'>
            <i className='bi bi-exclamation-triangle me-1'></i>
            Match contrassegnato come "No-Contest"
          </div>
        )}
        {!winner && (
          <div className='alert alert-info mt-2 py-2 small mb-0'>
            <i className='bi bi-info-circle me-1'></i>
            Puoi salvare il match adesso e impostare il vincitore in seguito.
          </div>
        )}
      </div>
    </div>
  )

  const renderActiveStep = () => {
    switch (activeStep) {
      case 'stipulation':
        return renderStipulationStep()
      case 'title':
        return renderTitleStep()
      case 'winner':
        return renderWinnerStep()
      default:
        return renderParticipantsStep()
    }
  }

  return (
    <div className='card shadow-sm border-0 show-editor-card show-match-form'>
      <div className='card-header bg-success text-white py-3 show-editor-card__header show-editor-card__header--success'>
        <h3 className='h5 mb-0'>
          <i className='bi bi-plus-circle me-2'></i>
          {editingMatchId ? 'Modifica Match' : 'Nuovo Match'}
        </h3>
      </div>
      <div className='card-body show-editor-card__body'>
        <form onSubmit={handleSubmit}>
          <div className='show-editor-summary show-editor-summary--stats mb-4'>
            <div className='show-editor-stat'>
              <span>Partecipanti</span>
              <strong>{isMultiTeamMatch ? totalTeamParticipants : matchParticipants.length}</strong>
              <small>{isMultiTeamMatch ? 'match a squadre' : 'match singolo o multi-man'}</small>
            </div>
            <div className='show-editor-stat'>
              <span>Stipulazione</span>
              <strong>{matchStipulation || 'Da confermare'}</strong>
              <small>{draftSelectedCount > 0 ? `${draftSelectedCount} dal draft` : 'pronta per il salvataggio'}</small>
            </div>
            <div className='show-editor-stat'>
              <span>Esito</span>
              <strong>{winner === 'no_contest' ? 'No-Contest' : winner ? 'Vincitore scelto' : 'Da definire'}</strong>
              <small>{editingMatchId ? 'modifica in corso' : 'nuovo match'}</small>
            </div>
          </div>

          {renderStepNav()}
          {renderActiveStep()}

          <div className='d-flex gap-2 pt-3 border-top show-editor-actions'>
            {editingMatchId && (
              <button
                type='button'
                className='btn btn-outline-secondary flex-grow-1'
                onClick={onCancel}
              >
                <i className='bi bi-x-circle me-1'></i>
                Annulla
              </button>
            )}
            {activeStepIndex > 0 && (
              <button
                type='button'
                className='btn btn-outline-light flex-grow-1'
                onClick={handleBack}
              >
                <i className='bi bi-arrow-left me-1'></i>
                Indietro
              </button>
            )}
            {activeStep !== 'winner' ? (
              <button
                type='button'
                className='btn btn-success flex-grow-1'
                onClick={handleNext}
                disabled={!canGoNext}
              >
                Avanti
                <i className='bi bi-arrow-right ms-1'></i>
              </button>
            ) : (
              <button
                type='submit'
                className={`btn ${editingMatchId ? 'btn-warning' : 'btn-success'} flex-grow-1`}
                disabled={!canSubmit}
              >
                <i className={`bi ${editingMatchId ? 'bi-arrow-repeat' : 'bi-save'} me-1`}></i>
                {editingMatchId ? 'Aggiorna Match' : 'Crea Match'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default MatchForm
